import React, { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";

/**
 * Mobile-first filter sheet for the Removal log. Renders as a
 * full-screen overlay on phones (one-hand grocery-floor use), with a
 * sticky Apply button at the bottom. ShelfLife typography + colors,
 * no shadows / glows / heavy animations.
 */

const DATE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

const REASONS = [
  { key: "expired", label: "Expired" },
  { key: "near_expiration", label: "Near expiration" },
  { key: "damaged", label: "Damaged" },
  { key: "sold_out", label: "Sold out" },
  { key: "other", label: "Other" },
];

export const EMPTY_FILTERS = Object.freeze({
  query: "",
  datePreset: "all",
  customFrom: "",
  customTo: "",
  reasons: [],     // empty = ALL reasons
  operators: [],   // empty = ALL operators
});

function ChipToggle({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`h-11 px-4 rounded-full border font-mono text-[12px] uppercase tracking-wider transition-colors ${
        active
          ? "bg-ink text-paper border-ink"
          : "bg-white text-ink/80 border-border/80 hover:border-ink/60"
      }`}
    >
      {children}
    </button>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-2">
      {children}
    </div>
  );
}

export default function RemovalsFilterSheet({
  open,
  onClose,
  initialFilters,
  operators,
  totalMatches,
  onApply,
}) {
  // Local working copy — applied only on Apply tap so users can dabble.
  const [draft, setDraft] = useState(initialFilters || EMPTY_FILTERS);
  useEffect(() => {
    if (open) setDraft(initialFilters || EMPTY_FILTERS);
  }, [open, initialFilters]);

  if (!open) return null;

  const toggleSet = (field, value) =>
    setDraft((d) => {
      const set = new Set(d[field]);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...d, [field]: Array.from(set) };
    });

  const handleClear = () => setDraft(EMPTY_FILTERS);
  const handleApply = () => { onApply(draft); onClose(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center sm:justify-center bg-ink/30 backdrop-blur-[2px]"
      data-testid="removals-filter-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-paper w-full sm:max-w-md sm:rounded-md sm:my-8 flex flex-col max-h-screen sm:max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-border/60 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="-ml-2 w-11 h-11 flex items-center justify-center text-ink/70 hover:text-ink"
            data-testid="filter-sheet-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <div className="font-display font-bold text-base">Search & filter</div>
          <button
            type="button"
            onClick={handleClear}
            className="font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink"
            data-testid="filter-sheet-clear"
          >
            Clear all
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-4 py-5 flex-1">
          {/* Search */}
          <FieldLabel>Search</FieldLabel>
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="text"
              autoFocus
              value={draft.query}
              onChange={(e) => setDraft((d) => ({ ...d, query: e.target.value }))}
              placeholder="Name, barcode, code, or note"
              className="w-full h-12 pl-10 pr-3 bg-white border border-border/80 rounded-md font-body text-sm focus:outline-none focus:border-ink"
              data-testid="filter-sheet-search-input"
            />
          </div>

          {/* Date range */}
          <FieldLabel>Date range</FieldLabel>
          <div className="flex flex-wrap gap-2 mb-3">
            {DATE_PRESETS.map((p) => (
              <ChipToggle
                key={p.key}
                active={draft.datePreset === p.key}
                onClick={() => setDraft((d) => ({ ...d, datePreset: p.key }))}
                testid={`filter-date-${p.key}`}
              >
                {p.label}
              </ChipToggle>
            ))}
          </div>
          {draft.datePreset === "custom" && (
            <div className="flex gap-2 mb-6">
              <label className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-1">From</div>
                <input
                  type="date"
                  value={draft.customFrom}
                  onChange={(e) => setDraft((d) => ({ ...d, customFrom: e.target.value }))}
                  className="w-full h-11 px-3 bg-white border border-border/80 rounded-md font-mono text-sm"
                  data-testid="filter-date-from"
                />
              </label>
              <label className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-1">To</div>
                <input
                  type="date"
                  value={draft.customTo}
                  onChange={(e) => setDraft((d) => ({ ...d, customTo: e.target.value }))}
                  className="w-full h-11 px-3 bg-white border border-border/80 rounded-md font-mono text-sm"
                  data-testid="filter-date-to"
                />
              </label>
            </div>
          )}
          <div className={draft.datePreset === "custom" ? "" : "mb-6"} />

          {/* Reasons */}
          <FieldLabel>Reason</FieldLabel>
          <div className="flex flex-wrap gap-2 mb-6">
            {REASONS.map((r) => (
              <ChipToggle
                key={r.key}
                active={draft.reasons.includes(r.key)}
                onClick={() => toggleSet("reasons", r.key)}
                testid={`filter-reason-${r.key}`}
              >
                {r.label}
              </ChipToggle>
            ))}
          </div>

          {/* Operators */}
          {operators.length > 0 && (
            <>
              <FieldLabel>Operator</FieldLabel>
              <div className="flex flex-wrap gap-2 mb-6">
                {operators.map((op) => (
                  <ChipToggle
                    key={op.id}
                    active={draft.operators.includes(op.id)}
                    onClick={() => toggleSet("operators", op.id)}
                    testid={`filter-operator-${op.id}`}
                  >
                    {op.label}
                  </ChipToggle>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sticky CTA */}
        <div className="border-t border-border/60 px-4 py-3 flex items-center gap-3 shrink-0">
          <div className="flex-1 font-mono text-[11px] uppercase tracking-wider text-ink/50">
            {totalMatches} matches in current data
          </div>
          <button
            type="button"
            onClick={handleApply}
            className="btn-primary h-12 px-6"
            data-testid="filter-sheet-apply"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
